import { memo } from 'react';
import { useCanvasPalette } from '../canvasPalette';
import {
  ROOM_FEATURE_LABELS,
  flattenOutline,
  openingGeometry,
  pointInPolygon,
  type RoomFeature,
  type RoomOpening,
  type RoomShape,
  type ShapePoint,
} from '@peles/shared';

/**
 * A room's shape drawn in SVG: its walls (curves included), the features that
 * belong to the room, and its doors and windows. One drawing, used by the
 * floor-plan canvas and the shape editor, so the room looks the same in both.
 *
 * `scale` is pixels per foot. Everything is drawn in the caller's coordinate
 * space starting at the room's own 0,0.
 */
export interface RoomShapeLayerProps {
  shape: RoomShape;
  scale: number;
  /** Draw feature names (pillars stay unlabelled — there are usually many). */
  showLabels?: boolean;
  /** Defaults to the canvas palette's (`CanvasPaletteContext`), as does the floor. */
  wallColor?: string;
  /** Behind the openings, to break the wall where a door is. */
  floorColor?: string;
}

const FEATURE_FILL: Record<RoomFeature['kind'], string> = {
  PILLAR: '#6b7280',
  BAR: '#a8a29e',
  STAGE: '#c4b5fd',
  DANCE_FLOOR: '#fde68a',
  RAISED: '#bfdbfe',
  SUNKEN: '#bbf7d0',
  NO_GO: '#fecaca',
};

export function outlinePath(shape: RoomShape, scale: number): string {
  const pts = flattenOutline(shape.outline);
  if (pts.length === 0) return '';
  return `${pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x * scale},${p.y * scale}`).join(' ')} Z`;
}

/**
 * Which side of a wall is the room: the unit normal pointing inside, found by
 * testing a point just off the wall. Doors "in" swing that way.
 */
export function inwardNormal(shape: RoomShape, centre: ShapePoint, angle: number): ShapePoint {
  const left = { x: -Math.sin(angle), y: Math.cos(angle) };
  const probe = { x: centre.x + left.x * 0.25, y: centre.y + left.y * 0.25 };
  return pointInPolygon(probe, flattenOutline(shape.outline)) ? left : { x: -left.x, y: -left.y };
}

/** The door leaves of an opening: hinge, closed end and open end of each. */
export function doorLeaves(shape: RoomShape, o: RoomOpening) {
  if (o.kind === 'WINDOW' || !o.swing) return [];
  const { start, end, centre, angle } = openingGeometry(shape, o);
  const inward = inwardNormal(shape, centre, angle);
  const dir = o.swing === 'IN' ? inward : { x: -inward.x, y: -inward.y };
  const leaf = (hinge: ShapePoint, closed: ShapePoint) => {
    const len = Math.hypot(closed.x - hinge.x, closed.y - hinge.y);
    return { hinge, closed, open: { x: hinge.x + dir.x * len, y: hinge.y + dir.y * len }, len };
  };
  if (o.kind === 'DOUBLE_DOOR') {
    const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    return [leaf(start, mid), leaf(end, mid)];
  }
  return o.hinge === 'END' ? [leaf(end, start)] : [leaf(start, end)];
}

function Opening({
  shape,
  opening,
  scale,
  floorColor,
  openingColor,
}: {
  shape: RoomShape;
  opening: RoomOpening;
  scale: number;
  floorColor: string;
  openingColor: string;
}) {
  const { start, end } = openingGeometry(shape, opening);
  const s = (p: ShapePoint) => ({ x: p.x * scale, y: p.y * scale });
  const a = s(start);
  const b = s(end);
  const isWindow = opening.kind === 'WINDOW';
  const exit = opening.kind === 'FIRE_EXIT';
  return (
    <g data-testid={`room-opening-${opening.id}`}>
      {/* Break the wall where the opening is. */}
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={floorColor} strokeWidth={5} />
      {isWindow ? (
        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#60a5fa" strokeWidth={3} />
      ) : (
        doorLeaves(shape, opening).map((leaf, i) => {
          const h = s(leaf.hinge);
          const c = s(leaf.closed);
          const o = s(leaf.open);
          const r = leaf.len * scale;
          // Sweep flag: the turn from closed to open, clockwise or not.
          const cross = (c.x - h.x) * (o.y - h.y) - (c.y - h.y) * (o.x - h.x);
          return (
            <g key={i} stroke={exit ? '#dc2626' : openingColor} fill="none" strokeWidth={1}>
              <line x1={h.x} y1={h.y} x2={o.x} y2={o.y} strokeWidth={1.5} />
              <path
                d={`M${c.x},${c.y} A${r},${r} 0 0 ${cross > 0 ? 1 : 0} ${o.x},${o.y}`}
                strokeDasharray="3 2"
              />
            </g>
          );
        })
      )}
      {exit && (
        <text
          x={(a.x + b.x) / 2}
          y={(a.y + b.y) / 2 - 4}
          textAnchor="middle"
          fontSize={9}
          fill="#dc2626"
          fontWeight={600}
        >
          EXIT
        </text>
      )}
    </g>
  );
}

function Feature({
  feature,
  scale,
  showLabels,
}: {
  feature: RoomFeature;
  scale: number;
  showLabels: boolean;
}) {
  const palette = useCanvasPalette();
  const x = feature.x * scale;
  const y = feature.y * scale;
  const w = feature.widthFt * scale;
  const h = feature.heightFt * scale;
  const fill = FEATURE_FILL[feature.kind];
  const noGo = feature.kind === 'NO_GO';
  const transform = feature.rotation
    ? `rotate(${feature.rotation}, ${x + w / 2}, ${y + h / 2})`
    : undefined;
  const common = {
    fill,
    fillOpacity: feature.kind === 'PILLAR' ? 1 : 0.55,
    stroke: noGo ? '#dc2626' : palette.featureEdge,
    strokeWidth: 1,
    strokeDasharray: noGo ? '4 3' : undefined,
  };
  return (
    <g transform={transform} data-testid={`room-feature-${feature.id}`}>
      {feature.shape === 'CIRCLE' ? (
        <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...common} />
      ) : (
        <rect x={x} y={y} width={w} height={h} {...common} />
      )}
      {showLabels && feature.kind !== 'PILLAR' && (
        <text
          x={x + w / 2}
          y={y + h / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={Math.max(8, Math.min(12, h / 3))}
          fill={palette.featureInk}
        >
          {feature.label || ROOM_FEATURE_LABELS[feature.kind]}
        </text>
      )}
    </g>
  );
}

export const RoomShapeLayer = memo(function RoomShapeLayer({
  shape,
  scale,
  showLabels = true,
  wallColor,
  floorColor,
}: RoomShapeLayerProps) {
  const palette = useCanvasPalette();
  const wall = wallColor ?? palette.wall;
  const floor = floorColor ?? palette.background;
  return (
    <g data-testid="room-shape-layer" pointerEvents="none">
      <path
        d={outlinePath(shape, scale)}
        fill="none"
        stroke={wall}
        strokeWidth={3}
        strokeLinejoin="round"
      />
      {shape.features.map((f) => (
        <Feature key={f.id} feature={f} scale={scale} showLabels={showLabels} />
      ))}
      {shape.openings
        .filter((o) => o.segment < shape.outline.length)
        .map((o) => (
          <Opening
            key={o.id}
            shape={shape}
            opening={o}
            scale={scale}
            floorColor={floor}
            openingColor={palette.opening}
          />
        ))}
    </g>
  );
});
